import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import { decidePublicConsent, viewPublicConsent } from '../teacher/teacherApi';
import './ParentConsentPage.css';

function ConsentShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader privacy="published" />
      {children}
      <AppFooter />
    </div>
  );
}

/**
 * Magic-link landing for the Phase 4.5c public-promote flow.
 *
 * Different from `ParentConsentPage`:
 *   * The kid is in a *teacher's classroom*, not a parent-linked
 *     account. The teacher already approved the work for the class.
 *   * The parent's call is whether to lift it from class-only into
 *     the public gallery. The work has already been seen by another
 *     adult (the teacher).
 *
 * Token is the auth. No signin required. Three rendered states:
 *   * pending — show project + kid name + classroom + Approve/Deny.
 *   * already-decided — outcome echoed back, back-to-studio link.
 *   * error — link expired / invalid / kid removed.
 */
export function PublicConsentPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const summary = useQuery({
    queryKey: ['public-consent', token],
    queryFn: () => viewPublicConsent(token),
    enabled: token.length > 0,
    retry: false,
  });

  const decision = useMutation({
    mutationFn: (which: 'approve' | 'deny') => decidePublicConsent(token, which),
    onSuccess: (data) => {
      qc.setQueryData(['public-consent', token], data);
    },
  });

  if (!token) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>This link is missing its token.</h1>
          <Button onClick={() => navigate('/')}>Go home</Button>
        </main>
      </ConsentShell>
    );
  }
  if (summary.isPending) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <p className="bl-consent__status" aria-live="polite">
            Loading…
          </p>
        </main>
      </ConsentShell>
    );
  }
  if (summary.error) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>This link isn&rsquo;t valid.</h1>
          <p className="bl-consent__lead">{summary.error.message}</p>
          <p className="bl-consent__lead">
            Ask the teacher to send a fresh link if you&rsquo;d still like to decide.
          </p>
          <Button onClick={() => navigate('/')}>Go home</Button>
        </main>
      </ConsentShell>
    );
  }
  const data = summary.data;
  if (!data) {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <p className="bl-consent__status">Loading…</p>
        </main>
      </ConsentShell>
    );
  }

  if (data.decision !== 'pending') {
    return (
      <ConsentShell>
        <main className="bl-consent">
          <h1>You already {data.decision} this.</h1>
          <p className="bl-consent__lead">
            {data.child_display_name}&rsquo;s teacher asked whether <em>{data.project_name}</em>{' '}
            from {data.classroom_name} could appear in the public gallery.
            {data.decision === 'approved' && ' You said yes — it’s now visible publicly.'}
            {data.decision === 'denied' && ' You said no — it stays just inside the class.'}
            {data.decision === 'expired' &&
              ' The link timed out. Ask the teacher to send a new one.'}
          </p>
          <Button onClick={() => navigate('/')}>Go home</Button>
        </main>
      </ConsentShell>
    );
  }

  return (
    <ConsentShell>
      <main className="bl-consent">
        <h1>{data.child_display_name}&rsquo;s teacher wants to share their work.</h1>
        <p className="bl-consent__lead">
          <strong>{data.child_display_name}</strong> made{' '}
          {/^[aeiou]/i.test(data.project_kind) ? 'an' : 'a'} {data.project_kind} project called{' '}
          <em>{data.project_name}</em> in <strong>{data.classroom_name}</strong>. Their teacher
          already approved it for the class. Now they&rsquo;re asking your okay to make it visible
          in the public gallery — outside the school — for anyone to see.
        </p>
        <p className="bl-consent__lead">
          Saying no keeps everything where it is: the project still shows up to your kid&rsquo;s
          class, just not on the public page. Saying yes adds it to the public gallery.
        </p>
        <div className="bl-consent__actions">
          <Button
            variant="secondary"
            onClick={() => decision.mutate('deny')}
            disabled={decision.isPending}
          >
            {decision.isPending && decision.variables === 'deny'
              ? 'Saving…'
              : 'Keep it just for the class'}
          </Button>
          <Button onClick={() => decision.mutate('approve')} disabled={decision.isPending}>
            {decision.isPending && decision.variables === 'approve'
              ? 'Saving…'
              : 'Yes, share publicly'}
          </Button>
        </div>
        {decision.error ? (
          <p className="bl-consent__error" role="alert">
            {decision.error instanceof Error ? decision.error.message : 'Something went wrong.'}
          </p>
        ) : null}
      </main>
    </ConsentShell>
  );
}
